import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useAppAuth } from '@/contexts/app-auth.context'
import { Skeleton } from '@/components/ui/skeleton'
import {
  directoryApiClient,
  type RoleRotation,
  type RoleWithMembers,
  type Staff,
} from '@/lib/api/directory.client'
import { directoryAdminApiClient } from '@/lib/api/directory-admin.client'
import RoleDetailPage from './RoleDetailPage'
import type { RoleSaveDraft } from './role-detail-types'

// Data-loading container for /settings/team/:roleId and /settings/team/new.
// The page is an explicit-save form: nothing persists until Save. For a new
// role the first Save creates it (name/description), then applies members,
// scheduling mode, and any rotation edits.
export default function RoleDetailRoute(props: { isNew?: boolean }) {
  const { isNew = false } = props
  const { roleId = '' } = useParams()
  const navigate = useNavigate()
  const { currentCompany, isSuperAdmin } = useAppAuth()
  const companyId = currentCompany?.id ?? ''

  const [role, setRole] = useState<RoleWithMembers | null>(null)
  const [allStaff, setAllStaff] = useState<Staff[]>([])
  const [rotation, setRotation] = useState<RoleRotation | null>(null)
  const [loading, setLoading] = useState(true)

  const backToRoles = useCallback(
    () => navigate('/settings/team?tab=roles'),
    [navigate],
  )

  const load = useCallback(async () => {
    if (!companyId) return
    try {
      const staffList = isSuperAdmin
        ? await directoryAdminApiClient.listStaff(companyId)
        : await directoryApiClient.listStaff()
      setAllStaff(staffList)

      if (isNew) return

      const [rolesList, rotationData] = await Promise.all([
        isSuperAdmin
          ? directoryAdminApiClient.listRoles(companyId)
          : directoryApiClient.listRoles(),
        isSuperAdmin
          ? directoryAdminApiClient.getRoleRotation(companyId, roleId)
          : directoryApiClient.getRoleRotation(roleId),
      ])
      const found = rolesList.find(candidate => candidate.id === roleId)
      if (!found) {
        toast.error('Role not found')
        backToRoles()
        return
      }
      setRole(found)
      setRotation(rotationData)
    } catch (error) {
      console.error(error)
      toast.error('Failed to load role')
      backToRoles()
    } finally {
      setLoading(false)
    }
  }, [companyId, roleId, isNew, isSuperAdmin, backToRoles])

  useEffect(() => {
    load()
  }, [load])

  // Persist the full draft: create-or-update details, reconcile members, then
  // apply rotation edits. Sequential on purpose — each step depends on the
  // role existing and membership being settled (rotation validates members).
  const saveDraft = async (targetRoleId: string, draft: RoleSaveDraft) => {
    if (isSuperAdmin) {
      await directoryAdminApiClient.updateRole(companyId, targetRoleId, {
        name: draft.name,
        // null clears the description (nullable column); don't coerce to ''.
        description: draft.description,
        schedulingMode: draft.schedulingMode,
      })
    } else {
      await directoryApiClient.updateRole(targetRoleId, {
        name: draft.name,
        description: draft.description,
        schedulingMode: draft.schedulingMode,
      })
    }

    const original = new Set(
      (role?.members ?? []).map(member => member.staff.id),
    )
    const selected = new Set(draft.memberStaffIds)
    const toAdd = draft.memberStaffIds.filter(id => !original.has(id))
    const toRemove = [...original].filter(id => !selected.has(id))
    for (const id of toAdd) {
      if (isSuperAdmin) {
        await directoryAdminApiClient.addRoleMember(companyId, targetRoleId, id)
      } else {
        await directoryApiClient.addRoleMember(targetRoleId, id)
      }
    }
    for (const id of toRemove) {
      if (isSuperAdmin) {
        await directoryAdminApiClient.removeRoleMember(
          companyId,
          targetRoleId,
          id,
        )
      } else {
        await directoryApiClient.removeRoleMember(targetRoleId, id)
      }
    }
    if (draft.memberStaffIds.length > 0) {
      if (isSuperAdmin) {
        await directoryAdminApiClient.reorderRoleMembers(
          companyId,
          targetRoleId,
          draft.memberStaffIds,
        )
      } else {
        await directoryApiClient.reorderRoleMembers(
          targetRoleId,
          draft.memberStaffIds,
        )
      }
    }

    const hasRotationChanges =
      draft.rotation.fallbackStaffId !== undefined ||
      draft.rotation.shiftBoundary !== undefined ||
      draft.rotation.closedDayStart !== undefined ||
      draft.rotation.days.length > 0
    if (hasRotationChanges) {
      const updated = isSuperAdmin
        ? await directoryAdminApiClient.updateRoleRotation(
            companyId,
            targetRoleId,
            draft.rotation,
          )
        : await directoryApiClient.updateRoleRotation(
            targetRoleId,
            draft.rotation,
          )
      setRotation(updated)
    }
  }

  const onSave = async (draft: RoleSaveDraft) => {
    try {
      if (isNew) {
        const created = isSuperAdmin
          ? await directoryAdminApiClient.createRole(companyId, {
              name: draft.name,
              description: draft.description ?? undefined,
            })
          : await directoryApiClient.createRole({
              name: draft.name,
              description: draft.description ?? undefined,
            })
        await saveDraft(created.id, draft)
        toast.success('Role created')
        navigate(`/settings/team/${created.id}`, { replace: true })
        return
      }
      await saveDraft(roleId, draft)
      toast.success('Role saved')
      await load()
    } catch (error) {
      console.error(error)
      // Surface the API's own message (duplicate name, validation, …) instead
      // of a generic failure.
      const detail =
        error instanceof Error &&
        error.message &&
        !error.message.startsWith('API request failed')
          ? String(error.message)
          : null
      toast.error(detail ?? 'Failed to save role')
      throw error
    }
  }

  if (loading || (!isNew && (!role || !rotation))) {
    return (
      <div className="max-w-4xl space-y-4 p-4 sm:p-8 mt-16">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    )
  }

  const assignmentsByDate: Record<string, string | null> = {}
  for (const day of rotation?.days ?? []) {
    assignmentsByDate[day.date] = day.staffId
  }

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden mt-16">
      <RoleDetailPage
        // Remount when the loaded role changes so form state re-initializes.
        key={isNew ? 'new' : (role?.id ?? roleId)}
        isNew={isNew}
        roleName={role?.name ?? ''}
        roleDescription={role?.description ?? null}
        allStaff={allStaff}
        members={(role?.members ?? []).map(member => member.staff)}
        mode={role?.schedulingMode ?? 'FAN_OUT'}
        rotation={{
          fallbackStaffId: rotation?.fallbackStaffId ?? null,
          shiftBoundary: rotation?.shiftBoundary ?? 'CALENDAR_DAY',
          closedDayStart: rotation?.closedDayStart ?? null,
          minEditableDate: rotation?.minEditableDate,
          assignmentsByDate,
        }}
        hasOfficeHours={
          !!currentCompany?.officeHours?.weeklySchedule &&
          !!currentCompany.timezoneName
        }
        onBack={backToRoles}
        onSave={onSave}
      />
    </div>
  )
}
